import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const studentAuth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('natur_student_token') || ''}` } });

// Caché simple en memoria para datos poco cambiantes (value lists, franquicias)
const cache = {};
async function cached(key, fn) {
  if (!cache[key]) cache[key] = fn();
  try { return await cache[key]; } catch (e) { delete cache[key]; throw e; }
}
export function invalidateCache(key) { delete cache[key]; }

export const api = {
  // Productos
  getProducts: (all = false) => axios.get(`${API}/products${all ? '?all=1' : ''}`).then((r) => r.data),
  getProductBySlug: (slug) => axios.get(`${API}/products/slug/${slug}`).then((r) => r.data),
  createProduct: (data) => axios.post(`${API}/products`, data).then((r) => r.data),
  updateProduct: (id, data) => axios.put(`${API}/products/${id}`, data).then((r) => r.data),

  // Ciudades (SEO Local)
  getCities: (all = false) => axios.get(`${API}/cities${all ? '?all=1' : ''}`).then((r) => r.data),
  getCityBySlug: (slug) => axios.get(`${API}/cities/slug/${slug}`).then((r) => r.data),
  createCity: (data) => axios.post(`${API}/cities`, data).then((r) => r.data),
  updateCity: (id, data) => axios.put(`${API}/cities/${id}`, data).then((r) => r.data),

  // Listados de Valores
  getValueLists: () => cached('valueLists', () => axios.get(`${API}/value-lists`).then((r) => r.data)),
  getValueListsFresh: () => axios.get(`${API}/value-lists`).then((r) => r.data),
  updateValueList: (id, data) => { invalidateCache('valueLists'); return axios.put(`${API}/value-lists/${id}`, data).then((r) => r.data); },

  // Franquicias
  getFranchises: () => cached('franchises', () => axios.get(`${API}/franchises`).then((r) => r.data)),
  getFranchisesFresh: () => axios.get(`${API}/franchises`).then((r) => r.data),
  createFranchise: (data) => { invalidateCache('franchises'); return axios.post(`${API}/franchises`, data).then((r) => r.data); },
  updateFranchise: (id, data) => { invalidateCache('franchises'); return axios.put(`${API}/franchises/${id}`, data).then((r) => r.data); },

  // Pedidos
  getOrders: () => axios.get(`${API}/orders`).then((r) => r.data),
  getOrder: (id) => axios.get(`${API}/orders/${id}`).then((r) => r.data),
  createOrder: (data) => axios.post(`${API}/orders`, data).then((r) => r.data),
  updateOrderStatus: (id, status) => axios.put(`${API}/orders/${id}/status`, { status }).then((r) => r.data),

  // Área Legal
  getLegalDocs: () => axios.get(`${API}/legal`).then((r) => r.data),
  createLegalDoc: (data) => axios.post(`${API}/legal`, data).then((r) => r.data),
  updateLegalDocMeta: (slug, data) => axios.put(`${API}/legal/${slug}/meta`, data).then((r) => r.data),
  reorderLegalDocs: (slugs) => axios.put(`${API}/legal/reorder`, { slugs }).then((r) => r.data),
  getLegalActive: () => cached('legalActive', () => axios.get(`${API}/legal/active`).then((r) => r.data)),
  getLegalActiveDoc: (slug) => axios.get(`${API}/legal/${slug}/active`).then((r) => r.data),
  createLegalVersion: (slug, content, clauses = null, author = 'MATRIZ') => axios.post(`${API}/legal/${slug}/versions`, { content, clauses, author }).then((r) => r.data),
  updateLegalVersion: (slug, version, content, clauses = null) => axios.put(`${API}/legal/${slug}/versions/${version}`, { content, clauses }).then((r) => r.data),
  publishLegalVersion: (slug, version, pin) => { invalidateCache('legalActive'); return axios.post(`${API}/legal/${slug}/publish`, { version }, { headers: { 'X-Master-Pin': pin } }).then((r) => r.data); },

  // Cupones
  getCoupons: () => axios.get(`${API}/coupons`).then((r) => r.data),
  createCoupon: (data) => axios.post(`${API}/coupons`, data).then((r) => r.data),
  updateCoupon: (id, data) => axios.put(`${API}/coupons/${id}`, data).then((r) => r.data),
  validateCoupon: (code, total) => axios.post(`${API}/coupons/validate`, { code, total }).then((r) => r.data),

  // Opiniones
  getReviews: (all = false) => axios.get(`${API}/reviews${all ? '?all=1' : ''}`).then((r) => r.data),
  createReview: (data) => axios.post(`${API}/reviews`, data).then((r) => r.data),
  updateReview: (id, data) => axios.put(`${API}/reviews/${id}`, data).then((r) => r.data),
  deleteReview: (id, pin) => axios.delete(`${API}/reviews/${id}`, { headers: { 'X-Master-Pin': pin } }).then((r) => r.data),
  deleteProduct: (id, pin) => axios.delete(`${API}/products/${id}`, { headers: { 'X-Master-Pin': pin } }).then((r) => r.data),

  // Repositorio Maestro: Plantillas
  getTemplates: () => axios.get(`${API}/templates`).then((r) => r.data),
  createTemplate: (data) => axios.post(`${API}/templates`, data).then((r) => r.data),
  updateTemplate: (id, data) => axios.put(`${API}/templates/${id}`, data).then((r) => r.data),
  previewTemplate: (id, vars = {}) => axios.post(`${API}/templates/${id}/preview`, vars).then((r) => r.data),

  // Historial de emails
  getEmailLog: () => axios.get(`${API}/email-log`).then((r) => r.data),

  // Contenido Web: banners y bloques promocionales
  getContentBlocks: (kind, all = false) => axios.get(`${API}/content-blocks?${kind ? `kind=${kind}&` : ''}${all ? 'all=1' : ''}`).then((r) => r.data),
  createContentBlock: (data) => axios.post(`${API}/content-blocks`, data).then((r) => r.data),
  updateContentBlock: (id, data) => axios.put(`${API}/content-blocks/${id}`, data).then((r) => r.data),
  deleteContentBlock: (id, pin) => axios.delete(`${API}/content-blocks/${id}`, { headers: { 'X-Master-Pin': pin } }).then((r) => r.data),

  // Archivos Multimedia (Biblioteca corporativa única)
  getMedia: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return axios.get(`${API}/media${qs ? `?${qs}` : ''}`).then((r) => r.data);
  },
  uploadMedia: (file, ownerId = 'matriz', folder = '') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('ownerId', ownerId);
    fd.append('user', 'MATRIZ');
    fd.append('folder', folder);
    return axios.post(`${API}/media/upload`, fd).then((r) => r.data);
  },
  getMediaFolders: () => axios.get(`${API}/media-folders`).then((r) => r.data),
  createMediaFolder: (name, parent = '') => axios.post(`${API}/media-folders`, { name, parent }).then((r) => r.data),
  renameMediaFolder: (id, name) => axios.put(`${API}/media-folders/${id}`, { name }).then((r) => r.data),
  moveMediaFolder: (id, parent) => axios.put(`${API}/media-folders/${id}`, { parent }).then((r) => r.data),
  duplicateMediaFolder: (id) => axios.post(`${API}/media-folders/${id}/duplicate`).then((r) => r.data),
  deleteMediaFolder: (id) => axios.delete(`${API}/media-folders/${id}`).then((r) => r.data),
  updateMedia: (id, data) => axios.put(`${API}/media/${id}`, data).then((r) => r.data),
  deleteMedia: (id, pin) => axios.delete(`${API}/media/${id}`, { headers: { 'X-Master-Pin': pin } }).then((r) => r.data),
  getMediaUsage: (id) => axios.get(`${API}/media/${id}/usage`).then((r) => r.data),
  getMediaStatus: () => axios.get(`${API}/media-status`).then((r) => r.data),
  mediaUrl: (id) => `${API}/media/${id}/content`,
  mediaThumb: (id, w = 240) => `${API}/media/${id}/content?w=${w}`,

  // Autenticación MATRIZ
  login: (email, password, recaptchaToken) => axios.post(`${API}/auth/login`, { email, password, recaptchaToken }).then((r) => r.data),
  getAuthConfig: () => axios.get(`${API}/auth/config`).then((r) => r.data),
  getMe: () => axios.get(`${API}/auth/me`).then((r) => r.data),
  confirmPin: (pin, recaptchaToken) => axios.post(`${API}/auth/confirm-pin`, { pin, recaptchaToken }).then((r) => r.data),
  changePassword: (currentPassword, newPassword, pin) => axios.post(`${API}/auth/change-password`, { currentPassword, newPassword }, { headers: { 'X-Master-Pin': pin } }).then((r) => r.data),
  changePin: (password, newPin) => axios.post(`${API}/auth/change-pin`, { password, newPin }).then((r) => r.data),
  getAccessLog: () => axios.get(`${API}/auth/access-log`).then((r) => r.data),

  // Portal del alumno (Área de Usuario)
  portalActivate: (token, pin, password) => axios.post(`${API}/portal/activate`, { token, pin, password }).then((r) => r.data),
  portalLogin: (email, password, recaptchaToken) => axios.post(`${API}/portal/login`, { email, password, recaptchaToken }).then((r) => r.data),
  portalMe: () => axios.get(`${API}/portal/me`, studentAuth()).then((r) => r.data),
  portalOrders: () => axios.get(`${API}/portal/orders`, studentAuth()).then((r) => r.data),
  portalDocuments: () => axios.get(`${API}/portal/documents`, studentAuth()).then((r) => r.data),
  portalUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/portal/documents`, fd, studentAuth()).then((r) => r.data);
  },
  portalDocBlob: (id) => axios.get(`${API}/portal/documents/${id}/content`, { ...studentAuth(), responseType: 'blob' }).then((r) => r.data),
  adminDocBlob: (id) => axios.get(`${API}/portal/documents/${id}/content`, { responseType: 'blob' }).then((r) => r.data),
  createAdmissionInvite: (aid) => axios.post(`${API}/admissions/${aid}/invite`).then((r) => r.data),
  getAdmissionDocuments: (aid) => axios.get(`${API}/admissions/${aid}/documents`).then((r) => r.data),
  getRecaptchaAdmin: () => axios.get(`${API}/security/recaptcha`).then((r) => r.data),
  updateRecaptcha: (data) => axios.put(`${API}/security/recaptcha`, data).then((r) => r.data),
  testRecaptcha: (secretKey, token) => axios.post(`${API}/security/recaptcha/test`, { secretKey, token }).then((r) => r.data),

  // Consentimientos y Cookies (GDPR)
  getScmeSettings: () => axios.get(`${API}/scme/settings`).then((r) => r.data),
  getScmeSettingsCached: () => {
    if (!api._scmeCache) api._scmeCache = axios.get(`${API}/scme/settings`).then((r) => r.data);
    return api._scmeCache;
  },
  updateScmeSettings: (data) => axios.put(`${API}/scme/settings`, data).then((r) => r.data),
  getChatStatus: () => axios.get(`${API}/chat/status`).then((r) => r.data),
  openChatConversation: () => axios.post(`${API}/chat/conversations`, {}).then((r) => r.data),
  sendChatMessage: (cid, text) => axios.post(`${API}/chat/conversations/${cid}/messages`, { text }).then((r) => r.data),
  logChatEvent: (type) => axios.post(`${API}/chat/events`, { type }).then((r) => r.data),
  getChatStats: () => axios.get(`${API}/chat/stats`).then((r) => r.data),
  createAdmission: (data) => axios.post(`${API}/admissions`, data).then((r) => r.data),
  getAdmissions: () => axios.get(`${API}/admissions`).then((r) => r.data),
  updateAdmission: (id, data) => axios.put(`${API}/admissions/${id}`, data).then((r) => r.data),
  getGoogleReviewsConfig: () => axios.get(`${API}/google-reviews/config`).then((r) => r.data),
  updateGoogleReviewsConfig: (data) => axios.put(`${API}/google-reviews/config`, data).then((r) => r.data),
  getMaintenance: () => axios.get(`${API}/maintenance`).then((r) => r.data),
  updateMaintenance: (data, pin) => axios.put(`${API}/maintenance`, data, { headers: pin ? { 'X-Master-Pin': pin } : {} }).then((r) => r.data),
  getMaintenanceLog: () => axios.get(`${API}/maintenance/log`).then((r) => r.data),
  getConsentConfig: () => axios.get(`${API}/consent/config`).then((r) => r.data),
  updateConsentConfig: (data, pin) => axios.put(`${API}/consent/config`, data, { headers: { 'X-Master-Pin': pin } }).then((r) => r.data),
  getConsentServices: () => axios.get(`${API}/consent/services`).then((r) => r.data),
  createConsentService: (data) => axios.post(`${API}/consent/services`, data).then((r) => r.data),
  updateConsentService: (id, data) => axios.put(`${API}/consent/services/${id}`, data).then((r) => r.data),
  deleteConsentService: (id, pin) => axios.delete(`${API}/consent/services/${id}`, { headers: { 'X-Master-Pin': pin } }).then((r) => r.data),
  registerConsent: (data) => axios.post(`${API}/consent`, data).then((r) => r.data),
  getConsents: () => axios.get(`${API}/consent`).then((r) => r.data),
  deleteConsent: (id, pin) => axios.delete(`${API}/consent/${id}`, { headers: { 'X-Master-Pin': pin } }).then((r) => r.data),
};

// Logo oficial recortado al contenido real (sin margen transparente) y optimizado (900px, PNG nítido)
export const LOGO_URL = '/natur-logo.png';

export const CONTACT = {
  phone: '+34 638 722 394',
  whatsapp: 'https://wa.me/34638722394?text=Hola%20quiero%20informaci%C3%B3n%20sobre%20el%20Permiso%20B',
  email: 'info@naturautoescuela.es',
};
